import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Mail, MailOpen, Paperclip, Trash2, Eye, CheckCircle, User, Clock, FileText, Image, Download, Send, MessageSquare, X } from 'lucide-react';
import { PageHeader } from '@/components/admin';
import { Button, Table, Badge, Modal, Card, Spinner, Input } from '@/components/common';
import { officeMessages as messagesApi } from '@/utils/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useMessages } from '@/hooks/useMessages';

const Nachrichten = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('alle');
  const [detailModal, setDetailModal] = useState({ open: false, message: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, message: null });
  const [replyText, setReplyText] = useState('');
  const [closeTicket, setCloseTicket] = useState(false); // NEW: Close ticket checkbox
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const { data: messagesData = [], isLoading: loading } = useMessages({ status: statusFilter });
  const messages = messagesData;

  const invalidateMessages = () => queryClient.invalidateQueries({ queryKey: ['messages'] });

  const markProcessedMutation = useMutation({
    mutationFn: (msg) => messagesApi.update(msg.id, { status: 'bearbeitet' }),
    onSuccess: (_, msg) => {
      toast.success('Nachricht als bearbeitet markiert');
      invalidateMessages();
      if (detailModal.open && detailModal.message?.id === msg.id) {
        setDetailModal({ open: false, message: null });
      }
    },
    onError: (err: any) => toast.error(err.message || 'Fehler beim Aktualisieren'),
  });

  const replyMutation = useMutation({
    mutationFn: ({ id, text, close }: { id: number; text: string; close: boolean }) =>
      messagesApi.reply(id, text, close),
    onSuccess: (_, { close }) => {
      toast.success(close ? 'Antwort gesendet und Ticket geschlossen' : 'Antwort gesendet');
      setReplyText('');
      setCloseTicket(false);
      invalidateMessages();
      setDetailModal({ open: false, message: null });
    },
    onError: (err: any) => toast.error(err.message || 'Fehler beim Senden der Antwort'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => messagesApi.delete(id),
    onSuccess: () => {
      toast.success('Nachricht gelöscht');
      setDeleteModal({ open: false, message: null });
      invalidateMessages();
    },
    onError: (err: any) => toast.error(err.message || 'Fehler beim Löschen'),
  });

  const reopenMutation = useMutation({
    mutationFn: async (msg) => {
      await messagesApi.reopen(msg.id);
      const updated = await messagesApi.getById(msg.id);
      return updated.data;
    },
    onSuccess: (updatedMessage) => {
      toast.success('Ticket wieder eröffnet');
      invalidateMessages();
      setDetailModal({ open: true, message: updatedMessage });
    },
    onError: (err: any) => {
      console.error('Error reopening ticket:', err);
      toast.error('Fehler beim Wiedereröffnen');
    },
  });

  const handleMarkProcessed = (msg) => markProcessedMutation.mutate(msg);

  const handleSendReply = () => {
    if (!detailModal.message || !replyText.trim()) return;
    replyMutation.mutate({ id: detailModal.message.id, text: replyText.trim(), close: closeTicket });
  };

  const handleDelete = () => {
    if (!deleteModal.message) return;
    deleteMutation.mutate(deleteModal.message.id);
  };

  const saving = markProcessedMutation.isPending || deleteMutation.isPending || reopenMutation.isPending;
  const sendingReply = replyMutation.isPending;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Check if user has responded (last message in conversation is from user)
  const hasUserResponse = (msg) => {
    if (!msg.conversation || msg.conversation.length === 0) return false;
    const lastEntry = msg.conversation[msg.conversation.length - 1];
    return lastEntry?.sender === 'user' && msg.status === 'unbearbeitet';
  };

  const getAttachmentUrl = (path) => {
    if (!path) return null;
    return path;
  };

  // Get attachment count for a message
  const getAttachmentCount = (msg) => {
    if (msg.attachment_paths && Array.isArray(msg.attachment_paths)) {
      return msg.attachment_paths.length;
    }
    if (msg.attachment_path) return 1;
    return 0;
  };

  const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];

  const isImagePath = (filePath) => {
    if (!filePath) return false;
    const lower = filePath.toLowerCase();
    return IMAGE_EXTENSIONS.some(ext => lower.endsWith(ext));
  };

  const getFileName = (filePath) => {
    if (!filePath) return 'Datei';
    return filePath.split('/').pop() || 'Datei';
  };

  const isAttachmentOnly = (text) => !text || text === '📎 Anhang';

  const renderAttachment = (att, idx, isUserBubble) => {
    if (isImagePath(att)) {
      return (
        <button
          key={idx}
          onClick={() => setLightboxSrc(att)}
          className="block cursor-pointer"
        >
          <img
            src={att}
            alt={`Anhang ${idx + 1}`}
            className="max-w-[200px] rounded-lg border border-white/20 hover:opacity-90 transition-opacity"
            loading="lazy"
          />
        </button>
      );
    }
    return (
      <a
        key={idx}
        href={att}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
          isUserBubble
            ? 'bg-white/10 text-white/90 hover:bg-white/20'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        }`}
      >
        <FileText className="w-5 h-5 flex-shrink-0" />
        <span className="truncate max-w-[160px]">{getFileName(att)}</span>
        <Download className="w-3.5 h-3.5 flex-shrink-0 ml-auto" />
      </a>
    );
  };

  const unreadCount = messages.filter(m => m.status === 'unbearbeitet').length;

  const filterTabs = [
    { value: 'alle', label: 'Alle', count: messages.length },
    { value: 'unbearbeitet', label: 'Unbearbeitet', count: unreadCount },
    { value: 'bearbeitet', label: 'Bearbeitet', count: messages.length - unreadCount },
  ];

  // Close modal and reset reply text
  const closeDetailModal = () => {
    setDetailModal({ open: false, message: null });
    setReplyText('');
    setCloseTicket(false);
  };

  return (
    <div>
      <PageHeader
        title="Nachrichten von Mitarbeitern"
        description="Info ans Büro - Nachrichten und Anhänge"
      />

      {/* Stats Card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-card-value text-yellow-600">{unreadCount}</p>
              <p className="stat-card-label">Unbearbeitet</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-50">
              <Mail className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-card-value text-green-600">{messages.length - unreadCount}</p>
              <p className="stat-card-label">Bearbeitet</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50">
              <MailOpen className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6 flex gap-2">
        {filterTabs.map((tab) => (
          <button
            key={tab.value}
            onClick={() => setStatusFilter(tab.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              statusFilter === tab.value
                ? 'bg-primary-500 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {tab.label}
            {tab.value === 'unbearbeitet' && tab.count > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                statusFilter === tab.value
                  ? 'bg-white text-primary-500'
                  : 'bg-yellow-500 text-white'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      <Table.Loading loading={loading}>
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.Header>Datum</Table.Header>
              <Table.Header>Mitarbeiter</Table.Header>
              <Table.Header>Betreff</Table.Header>
              <Table.Header>Anhänge</Table.Header>
              <Table.Header>Status</Table.Header>
              <Table.Header>Aktionen</Table.Header>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {messages.length === 0 ? (
              <Table.Empty message="Keine Nachrichten vorhanden" colSpan={6} />
            ) : (
              messages.map((msg) => {
                const attachmentCount = getAttachmentCount(msg);
                return (
                  <Table.Row
                    key={msg.id}
                    className={msg.status === 'unbearbeitet' ? 'bg-yellow-50' : ''}
                  >
                    <Table.Cell className="whitespace-nowrap">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock className="w-4 h-4" />
                        {formatDateTime(msg.created_at)}
                      </div>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/admin/mitarbeiter/${msg.user_id}`}
                          className="font-medium text-primary-600 hover:text-primary-800 flex items-center gap-1"
                        >
                          <User className="w-4 h-4" />
                          {msg.user_name || 'Unbekannt'}
                        </Link>
                        {/* Red dot indicator when user has responded */}
                        {hasUserResponse(msg) && (
                          <span
                            className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"
                            title="Mitarbeiter hat geantwortet"
                          />
                        )}
                      </div>
                    </Table.Cell>
                    <Table.Cell className="max-w-xs">
                      <button
                        onClick={() => setDetailModal({ open: true, message: msg })}
                        className="text-left hover:text-primary-600 transition-colors"
                      >
                        {/* Show subject if available, otherwise truncated message */}
                        {msg.subject || truncateText(msg.message_original || msg.message_german)}
                      </button>
                    </Table.Cell>
                    <Table.Cell>
                      {attachmentCount > 0 ? (
                        <button
                          onClick={() => setDetailModal({ open: true, message: msg })}
                          className="text-primary-500 hover:text-primary-700 flex items-center gap-1"
                        >
                          <Paperclip className="w-4 h-4" />
                          <span className="text-sm">{attachmentCount}</span>
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant={msg.status === 'unbearbeitet' ? 'warning' : 'success'}>
                        {msg.status === 'unbearbeitet' ? 'Unbearbeitet' : 'Bearbeitet'}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex gap-2">
                        {msg.status === 'unbearbeitet' && (
                          <Button
                            variant="outline"
                            size="sm"
                            icon={CheckCircle}
                            onClick={() => handleMarkProcessed(msg)}
                            loading={saving}
                          >
                            Erledigt
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          icon={Eye}
                          onClick={() => setDetailModal({ open: true, message: msg })}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          icon={Trash2}
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => setDeleteModal({ open: true, message: msg })}
                        />
                      </div>
                    </Table.Cell>
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>
      </Table.Loading>

      {/* Detail Modal */}
      <Modal
        isOpen={detailModal.open}
        onClose={closeDetailModal}
        title="Nachricht Details"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={closeDetailModal}>
              Schließen
            </Button>
            {detailModal.message?.status === 'unbearbeitet' && !replyText.trim() && (
              <Button
                variant="primary"
                icon={CheckCircle}
                onClick={() => handleMarkProcessed(detailModal.message)}
                loading={saving}
              >
                Als bearbeitet markieren
              </Button>
            )}
          </>
        }
      >
        {detailModal.message && (
          <div className="space-y-4">
            {/* Ticket Header */}
            <div className="p-3 bg-gray-50 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-primary-100">
                    <User className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium">{detailModal.message.user_name || 'Unbekannt'}</p>
                    <p className="text-xs text-gray-500">Ticket #{detailModal.message.id}</p>
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant={detailModal.message.status === 'unbearbeitet' ? 'warning' : 'success'}>
                    {detailModal.message.status === 'unbearbeitet' ? 'Offen' : 'Geschlossen'}
                  </Badge>
                  <p className="text-xs text-gray-500 mt-1">{formatDateTime(detailModal.message.created_at)}</p>
                </div>
              </div>
              {/* Subject line */}
              {detailModal.message.subject && (
                <div className="pt-2 border-t border-gray-200">
                  <p className="font-semibold text-gray-800">
                    {detailModal.message.subject}
                  </p>
                  {detailModal.message.source_language && detailModal.message.source_language !== 'de' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Original: {detailModal.message.source_language.toUpperCase()} |
                      {detailModal.message.message_german && ' Automatisch übersetzt'}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Conversation Thread - Chat Style */}
            <div className="border border-gray-200 rounded-lg">
              <div className="p-3 bg-gray-50 border-b border-gray-200">
                <h4 className="font-medium text-gray-700 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Konversation
                </h4>
              </div>
              <div className="p-4 max-h-96 overflow-y-auto space-y-4 bg-gray-100">
                {/* Render conversation thread - newest first */}
                {(detailModal.message.conversation && detailModal.message.conversation.length > 0) ? (
                  [...detailModal.message.conversation].reverse().map((entry, idx, arr) => {
                    // For user messages: get German translation from entry or fallback to ticket-level
                    // The first user message (last in reversed array) can use ticket's message_german
                    const isFirstUserMessage = entry.sender === 'user' && idx === arr.length - 1;
                    const germanText = entry.textGerman || (isFirstUserMessage ? detailModal.message.message_german : null);
                    const sourceLanguage = entry.language || (isFirstUserMessage ? detailModal.message.source_language : null);
                    const showGermanTranslation = entry.sender === 'user' &&
                      germanText &&
                      germanText !== entry.text &&
                      sourceLanguage &&
                      sourceLanguage !== 'de';

                    return (
                    <div
                      key={idx}
                      className={`flex ${entry.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg p-3 ${
                          entry.sender === 'user'
                            ? 'bg-primary-500 text-white'
                            : 'bg-white border border-gray-200 text-gray-800'
                        }`}
                      >
                        {/* Original message text (hide if attachment-only placeholder) */}
                        {!isAttachmentOnly(entry.text) && (
                          <p className="whitespace-pre-wrap text-sm">{entry.text}</p>
                        )}

                        {/* German translation for user messages */}
                        {showGermanTranslation && !isAttachmentOnly(entry.text) && (
                          <div className="mt-2 pt-2 border-t border-white/20">
                            <p className="text-xs text-white/70 italic">
                              🇩🇪 {germanText}
                            </p>
                          </div>
                        )}

                        {/* Translated reply for office messages */}
                        {entry.sender === 'office' && entry.textTranslated && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-xs text-gray-500 italic">
                              {entry.translatedLanguage === 'en' && '🇬🇧'}
                              {entry.translatedLanguage === 'tr' && '🇹🇷'}
                              {entry.translatedLanguage === 'uk' && '🇺🇦'}
                              {entry.translatedLanguage === 'pl' && '🇵🇱'}
                              {' '}{entry.textTranslated}
                            </p>
                          </div>
                        )}

                        {/* Attachments — thumbnails for images, file cards for documents */}
                        {entry.attachments && entry.attachments.length > 0 && (
                          <div className={`${isAttachmentOnly(entry.text) ? '' : 'mt-2'} flex flex-wrap gap-2`}>
                            {entry.attachments.map((att, attIdx) =>
                              renderAttachment(att, attIdx, entry.sender === 'user')
                            )}
                          </div>
                        )}
                        {/* Timestamp and sender info */}
                        <div
                          className={`mt-1 text-xs ${
                            entry.sender === 'user' ? 'text-white/70' : 'text-gray-500'
                          }`}
                        >
                          {new Date(entry.timestamp).toLocaleTimeString('de-DE', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                          {entry.sender === 'user' && sourceLanguage && sourceLanguage !== 'de' && (
                            <span> • {sourceLanguage.toUpperCase()}</span>
                          )}
                          {entry.sentBy && ` • ${entry.sentBy}`}
                          {entry.closedTicket && ' • Ticket geschlossen'}
                        </div>
                      </div>
                    </div>
                    );
                  })
                ) : (
                  /* Fallback: show old format if no conversation */
                  <div className="flex justify-end">
                    <div className="max-w-[80%] rounded-lg p-3 bg-primary-500 text-white">
                      {!isAttachmentOnly(detailModal.message.message_original) && (
                        <p className="whitespace-pre-wrap text-sm">{detailModal.message.message_original}</p>
                      )}
                      {/* Legacy attachments */}
                      {getAttachmentCount(detailModal.message) > 0 && (
                        <div className={`${isAttachmentOnly(detailModal.message.message_original) ? '' : 'mt-2'} flex flex-wrap gap-2`}>
                          {(detailModal.message.attachment_paths || [detailModal.message.attachment_path]).map((p, idx) =>
                            renderAttachment(getAttachmentUrl(p), idx, true)
                          )}
                        </div>
                      )}
                      <div className="mt-1 text-xs text-white/70">
                        {new Date(detailModal.message.created_at).toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  </div>
                )}
                {/* Legacy reply if exists but no conversation */}
                {detailModal.message.reply && (!detailModal.message.conversation || detailModal.message.conversation.length === 0) && (
                  <div className="flex justify-start">
                    <div className="max-w-[80%] rounded-lg p-3 bg-white border border-gray-200 text-gray-800">
                      <p className="whitespace-pre-wrap text-sm">{detailModal.message.reply}</p>
                      <div className="mt-1 text-xs text-gray-500">
                        {detailModal.message.replied_at && new Date(detailModal.message.replied_at).toLocaleTimeString('de-DE', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                        {detailModal.message.replied_by && ` • ${detailModal.message.replied_by}`}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Anhänge Galerie (nur wenn conversation leer und legacy attachments vorhanden) */}
            {(!detailModal.message.conversation || detailModal.message.conversation.length === 0) &&
             getAttachmentCount(detailModal.message) > 0 && (
              <div>
                <h4 className="font-medium text-gray-700 mb-2">
                  Anhänge ({getAttachmentCount(detailModal.message)}):
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  {(detailModal.message.attachment_paths || [detailModal.message.attachment_path]).map((path, idx) => {
                    const type = detailModal.message.attachment_types?.[idx] ||
                                 detailModal.message.attachment_type || 'photo';
                    return (
                      <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                        {type === 'photo' ? (
                          <div>
                            <img
                              src={getAttachmentUrl(path)}
                              alt={`Anhang ${idx + 1}`}
                              className="w-full h-40 object-cover"
                            />
                            <a
                              href={getAttachmentUrl(path)}
                              download
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-2 p-2 text-sm text-primary-600 hover:bg-gray-50"
                            >
                              <Download className="w-4 h-4" />
                              Herunterladen
                            </a>
                          </div>
                        ) : (
                          <a
                            href={getAttachmentUrl(path)}
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-4 hover:bg-gray-50 transition-colors"
                          >
                            <FileText className="w-8 h-8 text-gray-400" />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-700">Dokument {idx + 1}</p>
                              <p className="text-xs text-gray-500">Klicken zum Herunterladen</p>
                            </div>
                            <Download className="w-4 h-4 text-primary-600" />
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Antwort-Feld (nur wenn Ticket noch offen) */}
            {detailModal.message.status === 'unbearbeitet' && (
              <div className="border-t pt-4">
                <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Antwort senden:
                </h4>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Ihre Antwort eingeben..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  rows={3}
                />
                {/* Ticket Close Checkbox */}
                <div className="flex items-center gap-2 mt-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="closeTicket"
                    checked={closeTicket}
                    onChange={(e) => setCloseTicket(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="closeTicket" className="text-sm text-gray-700 cursor-pointer">
                    <span className="font-medium">Ticket nach Antwort schließen (Erledigt)</span>
                    <span className="block text-xs text-gray-500 mt-0.5">
                      Wenn nicht aktiviert, kann der Mitarbeiter auf Ihre Antwort reagieren
                    </span>
                  </label>
                </div>
                <div className="flex justify-end mt-3">
                  <Button
                    variant="primary"
                    icon={Send}
                    onClick={handleSendReply}
                    loading={sendingReply}
                    disabled={!replyText.trim()}
                  >
                    {closeTicket ? 'Antwort senden & Ticket schließen' : 'Antwort senden'}
                  </Button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {closeTicket
                    ? 'Die Antwort wird gesendet und das Ticket wird als erledigt markiert.'
                    : 'Die Antwort wird gesendet. Der Mitarbeiter kann darauf antworten.'}
                </p>
              </div>
            )}

            {/* Ticket geschlossen Info + Reopen Button */}
            {detailModal.message.status === 'bearbeitet' && (
              <div className="pt-2 border-t space-y-3">
                {detailModal.message.processed_by && (
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    Ticket geschlossen von {detailModal.message.processed_by} am{' '}
                    {formatDateTime(detailModal.message.processed_at)}
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => reopenMutation.mutate(detailModal.message)}
                  loading={reopenMutation.isPending}
                >
                  🔓 Ticket wieder eröffnen
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, message: null })}
        title="Nachricht löschen"
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, message: null })}>
              Abbrechen
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={saving}>
              Löschen
            </Button>
          </>
        }
      >
        <p className="text-gray-600">
          Möchten Sie diese Nachricht von <strong>{deleteModal.message?.user_name}</strong> wirklich löschen?
          Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
      </Modal>

      {/* Image Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxSrc(null)}
        >
          <button
            onClick={() => setLightboxSrc(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxSrc}
            alt="Vollbild"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default Nachrichten;
